import * as React from "react";
import { useRef, useState } from "react";
import {
  Button,
  ClickAwayListener,
  MenuItem,
  Paper,
  Popper,
  styled,
} from "@mui/material";
import { SupportedLanguagesNamesAndIcons } from "@kahunmedical/common-configuration";
import { useLocaleContext } from "fe-common/locale/localeContext";
import CheckIcon from "@mui/icons-material/Check";
import { CommonColors } from "fe-web-components/colors/commonColors";
import { useHistory } from "react-router";
import { PatientRoute } from "../PatientRoutes/PatientRoute";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { PatientColorMap } from "./patientColorMap";
import clsx from "clsx";
import { NavBarZIndex } from "fe-common/ui/stacking";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useClinicData } from "../clinic/clinicHooks";
//region [[ Styles ]]
const MENU_WIDTH = 165;
const Z_INDEX = NavBarZIndex - 2;

const LanguageChangerView = styled("div")({
  position: "absolute",
  right: 15,
  top: 15,
  zIndex: Z_INDEX,
});

const LanguageSelectorButton = styled(Button)({
  direction: "ltr",
  borderRadius: "8px",
  border: `1px solid ${PatientColorMap.White}`,
  width: "fit-content",
  minWidth: "fit-content",
  padding: 0,
  margin: 0,
  "&, &:hover": {
    background: PatientColorMap.White,
    color: PatientColorMap.Primary,
  },
  // "&:hover": {
  //   background: "#dcdcdc",
  //   color: PatientColorMap.Primary,
  // },
  "& span": {
    textTransform: "none",
    justifyContent: "space-between",
    margin: 0,
    padding: 0,
  },
  "&.open": {
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
  },
});

const LanguageMenuItem = styled(MenuItem)({
  "&.selected": {
    background: CommonColors.Primary,
    color: CommonColors.White,
    fill: CommonColors.White,
    "&:first-of-type": {
      borderTopRightRadius: "inherit",
      borderTopLeftRadius: "inherit",
    },
    "&:last-of-type": {
      borderBottomRightRadius: "inherit",
      borderBottomLeftRadius: "inherit",
    },
  },
});

const FlagContainer = styled("span")({
  fontSize: "15px",
  // Win specificity over button's span css
  "&.icon": {
    padding: "0 5px",
  },
});

const CheckContainer = styled("span")({
  width: "30px",
});

const DropDownWrapper = styled(Paper)({
  width: MENU_WIDTH,
  marginLeft: "1px",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface LanguageChangerProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const LanguageChanger = ({ ...props }: LanguageChangerProps) => {
  const [isMenuShown, setIsMenuShown] = useState(false);
  const containerRef = useRef();
  const { changeLocale } = useLocaleContext();
  const { locale } = useLocaleContext();
  const history = useHistory();
  const {
    clinic: clinicId,
    name: clinicName,
    clinicSettings,
  } = useClinicData();

  const supportedLanguages = clinicSettings.supportedLanguages || [];

  const isLocaleChangerFlagActive =
    usePatientFeatureFlags("localeChanger") && supportedLanguages?.length;

  const showLanguageSelect = (e) => {
    console.log("showLanguageSelect", e);
    setIsMenuShown(true);
  };

  const createChangeLanguageHandler = (lang: string) => (e) => {
    evt({
      category: CATEGORY.PATIENT_CHAT,
      action: ACTION.CHANGE_LOCALE,
      props: {
        clinicId,
        clinicName,
        defaultLanguage: clinicSettings.defaultLocale,
        selectedLanguage: lang,
      },
    });
    changeLocale(lang);
    setIsMenuShown(false);
  };

  if (!isLocaleChangerFlagActive) return null;

  if (history.location.pathname.includes(PatientRoute.Questions)) return null;

  return (
    <ClickAwayListener onClickAway={() => setIsMenuShown(false)}>
      <LanguageChangerView>
        <LanguageSelectorButton
          className={clsx(
            "language-selector-button",
            isMenuShown ? "open" : "closed",
          )}
          ref={containerRef}
          onClick={showLanguageSelect}
          endIcon={<ArrowDropDownIcon />}
        >
          <FlagContainer className={"icon"}>
            {SupportedLanguagesNamesAndIcons[locale].icon}
          </FlagContainer>
        </LanguageSelectorButton>
        <Popper
          id={"language-changer"}
          open={isMenuShown}
          anchorEl={containerRef.current}
          style={{ zIndex: Z_INDEX }}
        >
          <DropDownWrapper>
            {Object.entries(supportedLanguages).map(([name, langKey], i) => (
              <LanguageMenuItem
                className={locale === langKey ? "selected" : ""}
                key={langKey}
                onClick={createChangeLanguageHandler(langKey)}
              >
                <CheckContainer>
                  {locale === langKey ? (
                    <CheckIcon />
                  ) : (
                    SupportedLanguagesNamesAndIcons[langKey]?.icon
                  )}
                </CheckContainer>
                {SupportedLanguagesNamesAndIcons[langKey]?.name}
              </LanguageMenuItem>
            ))}
          </DropDownWrapper>
        </Popper>
      </LanguageChangerView>
    </ClickAwayListener>
  );
};
